import React, { useCallback, useContext, useEffect, useState } from "react";
import { Form, Input, Button, Modal, Select, Radio, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import {
  createCustomer,
  updateCustomer,
  getCities,
  getRegions,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { AppContext } from "../../../context/AppContextProvider";
import {
  arabicNameRules,
  cleanArabicInput,
  fourDigitNumberRules,
  postalCodeRules,
  saVatNumberRules,
} from "../../../utils/validators";
import { handleApiError } from "../../../utils/errorHandler";

const CustomerFormModal = ({
  open,
  mode = "add",
  initialValues = null,
  onClose,
  onSuccess,
}) => {
  const { appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const [customerForm] = Form.useForm();

  const [loadingBTN, setLoadingBTN] = useState(false);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [userType, setUserType] = useState("individual");
  const selectedRegionId = Form.useWatch("region_id", customerForm);

  const isEdit = mode === "edit";
  const isCompany = userType === "company";
  const title = isEdit ? t("customer_edit") : t("customer_add");
  const submitLabel = isEdit ? t("update") : t("customer_add");

  const handleCityRequestOnLoad = useCallback(
    (value) => {
      if (!value) return;
      setCitiesLoading(true);
      getCities(value)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setCities(response?.data?.data?.cities || []);
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => {
          setCitiesLoading(false);
        });
    },
    [t],
  );

  useEffect(() => {
    getRegions()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setRegions(response?.data?.data?.regions || []);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    if (!open) return;

    customerForm.resetFields();

    if (isEdit && initialValues) {
      customerForm.setFieldsValue({
        id: initialValues.id,
        name_ar: initialValues.name_ar,
        name_en: initialValues.name_en,
        phone: initialValues.phone,
        user_type: initialValues.user_type,
        vat: initialValues.vat,
        cr: initialValues.cr,
        city_id: initialValues.city?.id,
        region_id: initialValues.region?.id,
        country: initialValues.country || "Saudi Arabia",
        postal_code: initialValues.postal_code,
        street: initialValues.street,
        building_number: initialValues.building_number,
        plot_id_number: initialValues.plot_id_number,
      });
      setUserType(initialValues.user_type || "individual");

      if (initialValues.region?.id) {
        handleCityRequestOnLoad(initialValues.region.id);
      } else {
        setCities([]);
      }
    } else {
      customerForm.setFieldsValue({
        user_type: "individual",
        country: "Saudi Arabia",
      });
      setUserType("individual");
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, initialValues]);

  const handleRegionSelect = (value) => {
    customerForm.resetFields(["city_id"]);
    setCities([]);

    if (!value) {
      setCitiesLoading(false);
      return;
    }

    setCitiesLoading(true);
    getCities(value)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setCities(response?.data?.data?.cities || []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setCitiesLoading(false);
      });
  };

  const handleArabicNameChange = (e) => {
    customerForm.setFieldsValue({
      name_ar: cleanArabicInput(e.target.value),
    });
  };

  const handleSubmit = () => {
    customerForm
      .validateFields()
      .then((values) => {
        setLoadingBTN(true);

        const apiCall = isEdit
          ? updateCustomer(values)
          : createCustomer(values);

        apiCall
          .then((response) => {
            if (response.data.success === true) {
              NotificationWithIcon(
                "success",
                isEdit ? t("customer_updated") : t("customer_added"),
                isEdit
                  ? t("customer_updated_successfully")
                  : t("customer_added_successfully"),
              );
              customerForm.resetFields();
              onSuccess?.(response, values);
              onClose?.();
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleCancel = () => {
    customerForm.resetFields();
    onClose?.();
  };

  const regionOptions = regions.map((region) => ({
    label: appConfig.direction === "ltr" ? region.name_en : region.name_ar,
    value: region.id,
  }));

  const cityOptions = cities.map((city) => ({
    label: appConfig.direction === "ltr" ? city.name_en : city.name_ar,
    value: city.id,
  }));

  const filterByLabel = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <Modal
      open={open}
      title={title}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button
          type="default"
          key="back"
          loading={loadingBTN}
          onClick={handleCancel}
        >
          {t("cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loadingBTN}
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>,
      ]}
    >
      <Form
        name="customer-form"
        layout="vertical"
        onFinish={handleSubmit}
        className="app-form-full"
        form={customerForm}
      >
        <Row gutter={[20, 10]}>
          <Col span={12}>
            <Form.Item
              name="name_ar"
              label={t("name_ar")}
              dependencies={["phone", "user_type"]}
              validateFirst
              rules={[
                ...arabicNameRules(t, false),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const userType = getFieldValue("user_type");
                    const phone = getFieldValue("phone");

                    if (userType === "company") {
                      if (!value?.trim()) {
                        return Promise.reject(
                          new Error(t("validate_name_required")),
                        );
                      }
                    }

                    if (userType === "individual" && !value?.trim() && !phone) {
                      return Promise.reject(
                        new Error(t("validate_name_or_phone_required")),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              hasFeedback
            >
              <Input
                dir="rtl"
                placeholder={t("arabic_only_validation")}
                onChange={handleArabicNameChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="name_en" label={t("name_en")} hasFeedback>
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="phone"
              label={t("phone_number") + " eg: +966 5xxxxxxx"}
              dependencies={["name_ar", "user_type"]}
              rules={[
                { min: 12, message: t("validate_phone_exact_limit") },
                { max: 12, message: t("validate_phone_exact_limit") },

                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const userType = getFieldValue("user_type");
                    const name = getFieldValue("name_ar");

                    if (userType === "individual" && !value && !name?.trim()) {
                      return Promise.reject(
                        new Error(t("validate_name_or_phone_required")),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              style={{ textAlign: "left" }}
              hasFeedback
            >
              <PhoneInput
                localization={["sa"]}
                masks={{ sa: "(...) ..-..-.." }}
                placeholder="+966 (123) 45-67-89"
                country={"sa"}
                onlyCountries={["sa"]}
                style={{ width: "100%", direction: "ltr" }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="user_type"
              label={t("type")}
              rules={[{ required: true, message: t("validate_type_required") }]}
            >
              <Radio.Group onChange={(e) => setUserType(e.target.value)}>
                <Radio value="individual">{t("individual")}</Radio>
                <Radio value="company">{t("company")}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {isCompany && (
            <>
              <Col span={8}>
                <Form.Item
                  name="vat"
                  label={t("vat_number")}
                  rules={saVatNumberRules(t, true)}
                  hasFeedback
                >
                  <Input maxLength={15} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="cr"
                  label={t("cr_number")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_cr_number_required"),
                    },
                    {
                      min: 10,
                      message: t("validate_cr_number_exact_limit"),
                    },
                    {
                      max: 10,
                      message: t("validate_cr_number_exact_limit"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input maxLength={10} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="country"
                  label={t("country")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_country_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input disabled />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="region_id"
                  label={t("region")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_region_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    showSearch
                    onChange={handleRegionSelect}
                    options={regionOptions}
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="city_id"
                  label={t("city")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_city_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    showSearch
                    loading={citiesLoading}
                    disabled={!selectedRegionId || citiesLoading}
                    options={cityOptions}
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="postal_code"
                  label={t("postal_code")}
                  validateFirst
                  validateTrigger={["onBlur", "onChange"]}
                  rules={postalCodeRules(t, true)}
                  hasFeedback
                >
                  <Input maxLength={5} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="street"
                  label={t("street")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_street_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="building_number"
                  label={t("building_number")}
                  validateFirst
                  validateTrigger={["onBlur", "onChange"]}
                  rules={fourDigitNumberRules(
                    t,
                    true,
                    "validate_building_number_required",
                    "validate_building_number_4digits",
                  )}
                  hasFeedback
                >
                  <Input maxLength={4} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="plot_id_number"
                  label={t("plot_id_number")}
                  validateFirst
                  validateTrigger={["onBlur", "onChange"]}
                  rules={fourDigitNumberRules(
                    t,
                    true,
                    "validate_plot_id_number_required",
                    "validate_plot_identification_4digits",
                  )}
                  hasFeedback
                >
                  <Input maxLength={4} inputMode="numeric" />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Form.Item name="id" className="app-hidden">
          <input type="hidden" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CustomerFormModal;
